import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, Eye, BarChart3, Shield, Target, Home, Briefcase,
  Heart, FileText, Calendar, Clock, AlertTriangle, ChevronRight,
  RefreshCw, CheckCircle2, UserCheck
} from 'lucide-react';

const ENGAGEMENT_COLORS = {
  high: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-red-50 text-red-700 border-red-200',
};

export default function AgencyViewPage() {
  const { axiosAuth } = useAuth();
  const [caseload, setCaseload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantDetail, setParticipantDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clients, setClients] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      const [caseloadRes, clientsRes] = await Promise.all([
        api.get('/agency/caseload-summary'),
        api.get('/users?role=client').catch(() => ({ data: [] }))
      ]);
      setCaseload(caseloadRes.data);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
    } catch {
      toast.error('Failed to load caseload data');
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { loadData(); }, [loadData]);

  const viewParticipant = async (participantId) => {
    setDetailLoading(true);
    setSelectedParticipant(participantId);
    try {
      const api = axiosAuth();
      const [summaryRes, snapshotRes] = await Promise.all([
        api.get(`/agency/participant-summary/${participantId}`),
        api.get(`/snapshot/${participantId}`).catch(() => ({ data: null }))
      ]);
      setParticipantDetail({
        ...summaryRes.data,
        snapshot: snapshotRes.data
      });
    } catch {
      toast.error('Failed to load participant details');
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agency-view-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Agency Oversight
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor participant progress and compliance</p>
        </div>
        <Button variant="outline" onClick={loadData} className="rounded-full gap-2" data-testid="refresh-agency-btn">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Caseload Summary */}
      {caseload && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-4 text-center">
              <Users size={24} className="mx-auto mb-2 text-teal-600" />
              <p className="text-2xl font-bold" data-testid="total-participants-count">{caseload.total_participants}</p>
              <p className="text-xs text-muted-foreground">Total Participants</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-xl bg-emerald-50">
            <CardContent className="p-4 text-center">
              <CheckCircle2 size={24} className="mx-auto mb-2 text-emerald-600" />
              <p className="text-2xl font-bold text-emerald-700">{caseload.compliance_summary?.high_engagement || 0}</p>
              <p className="text-xs text-emerald-600">High Engagement</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-xl bg-amber-50">
            <CardContent className="p-4 text-center">
              <Clock size={24} className="mx-auto mb-2 text-amber-600" />
              <p className="text-2xl font-bold text-amber-700">{caseload.compliance_summary?.medium_engagement || 0}</p>
              <p className="text-xs text-amber-600">Medium Engagement</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-xl bg-red-50">
            <CardContent className="p-4 text-center">
              <AlertTriangle size={24} className="mx-auto mb-2 text-red-500" />
              <p className="text-2xl font-bold text-red-600">{caseload.compliance_summary?.low_engagement || 0}</p>
              <p className="text-xs text-red-500">Low Engagement</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Engagement Breakdown */}
      {caseload && caseload.total_participants > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 size={16} className="text-teal-600" /> Engagement Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 h-8 rounded-full overflow-hidden">
              {caseload.engagement_rates?.high_pct > 0 && (
                <div
                  className="bg-emerald-500 flex items-center justify-center text-xs text-white font-medium"
                  style={{ width: `${caseload.engagement_rates.high_pct}%` }}
                >
                  {caseload.engagement_rates.high_pct}%
                </div>
              )}
              {caseload.engagement_rates?.medium_pct > 0 && (
                <div
                  className="bg-amber-400 flex items-center justify-center text-xs text-white font-medium"
                  style={{ width: `${caseload.engagement_rates.medium_pct}%` }}
                >
                  {caseload.engagement_rates.medium_pct}%
                </div>
              )}
              {caseload.engagement_rates?.low_pct > 0 && (
                <div
                  className="bg-red-400 flex items-center justify-center text-xs text-white font-medium"
                  style={{ width: `${caseload.engagement_rates.low_pct}%` }}
                >
                  {caseload.engagement_rates.low_pct}%
                </div>
              )}
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> High</div>
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Medium</div>
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-red-400" /> Low</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participant List */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <UserCheck size={16} className="text-teal-600" /> Participants ({clients.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="text-center py-8">
              <Users size={40} className="mx-auto text-slate-200 mb-3" />
              <p className="text-sm text-muted-foreground">No participants in your caseload</p>
            </div>
          ) : (
            <div className="space-y-2">
              {clients.map(c => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => viewParticipant(c.id)}
                  data-testid={`participant-row-${c.id}`}
                >
                  <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center">
                    <Users size={16} className="text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{c.full_name}</p>
                    <p className="text-xs text-muted-foreground">{c.email}</p>
                  </div>
                  <Badge className={`border-0 text-xs ${c.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {c.status || 'active'}
                  </Badge>
                  <ChevronRight size={16} className="text-slate-300" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Participant Detail Modal */}
      <Dialog open={!!selectedParticipant} onOpenChange={v => { if (!v) { setSelectedParticipant(null); setParticipantDetail(null); } }}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Participant Summary</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : participantDetail ? (
            <Tabs defaultValue="overview">
              <TabsList className="grid grid-cols-3 h-auto p-1 bg-slate-100 rounded-xl">
                <TabsTrigger value="overview" className="rounded-lg text-xs py-1.5 data-[state=active]:bg-white">Overview</TabsTrigger>
                <TabsTrigger value="engagement" className="rounded-lg text-xs py-1.5 data-[state=active]:bg-white">Engagement</TabsTrigger>
                <TabsTrigger value="snapshot" className="rounded-lg text-xs py-1.5 data-[state=active]:bg-white">Case Snapshot</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-3 space-y-3">
                <div className="p-4 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50">
                  <p className="font-bold text-lg">{participantDetail.participant?.name}</p>
                  <p className="text-xs text-muted-foreground">{participantDetail.participant?.email}</p>
                  <Badge className="mt-1 bg-teal-100 text-teal-700 border-0 text-xs">{participantDetail.participant?.status}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 rounded-xl bg-slate-50 text-center">
                    <p className="text-lg font-bold">{participantDetail.milestone_completion?.milestones_completed || 0}</p>
                    <p className="text-xs text-muted-foreground">Milestones</p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 text-center">
                    <p className="text-lg font-bold">{participantDetail.documents_on_file || 0}</p>
                    <p className="text-xs text-muted-foreground">Documents</p>
                  </div>
                </div>

                {participantDetail.program_enrollment?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Programs</p>
                    <div className="flex gap-1 flex-wrap">
                      {participantDetail.program_enrollment.map((p, i) => (
                        <Badge key={`item-${i}`} className="bg-teal-50 text-teal-700 border-0 text-xs">{p.name}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="engagement" className="mt-3 space-y-3">
                <div className="p-4 rounded-xl bg-emerald-50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-emerald-800">Engagement Rate</span>
                    <span className="font-bold text-emerald-700">{participantDetail.engagement_summary?.engagement_rate}</span>
                  </div>
                  <p className="text-xs text-emerald-600">{participantDetail.engagement_summary?.checkins_completed} check-ins in last 30 days</p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50">
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpen size={14} className="text-teal-600" />
                    <span className="text-sm font-medium">Group Sessions</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{participantDetail.attendance_summary?.group_sessions_attended || 0} attended (last 30 days)</p>
                </div>

                <div className="p-3 rounded-xl bg-slate-50">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase size={14} className="text-amber-600" />
                    <span className="text-sm font-medium">Services Received</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {participantDetail.services_received?.total_services || 0} services ({participantDetail.services_received?.total_hours || 0}h)
                  </p>
                  {participantDetail.services_received?.service_types?.length > 0 && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {participantDetail.services_received.service_types.map((t, i) => (
                        <Badge key={`item-${i}`} className="bg-slate-200 text-slate-600 border-0 text-[10px]">{t.replace(/_/g, ' ')}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="snapshot" className="mt-3 space-y-3">
                {participantDetail.snapshot ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-3 rounded-xl bg-teal-50 text-center">
                        <p className="text-xs text-teal-600 mb-0.5">Roadmap Stage</p>
                        <p className="text-sm font-semibold text-teal-800">{participantDetail.snapshot.roadmap_stage}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-blue-50 text-center">
                        <p className="text-xs text-blue-600 mb-0.5">Housing</p>
                        <p className="text-sm font-semibold text-blue-800">{participantDetail.snapshot.housing_status}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-amber-50 text-center">
                        <p className="text-xs text-amber-600 mb-0.5">Employment</p>
                        <p className="text-sm font-semibold text-amber-800 capitalize">{participantDetail.snapshot.employment_status}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-purple-50 text-center">
                        <p className="text-xs text-purple-600 mb-0.5">Engagement</p>
                        <p className="text-sm font-semibold text-purple-800 capitalize">{participantDetail.snapshot.recent_engagement?.engagement_level}</p>
                      </div>
                    </div>

                    {participantDetail.snapshot.active_goals?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Active Goals</p>
                        <div className="space-y-1.5">
                          {participantDetail.snapshot.active_goals.map(g => (
                            <div key={g.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                              <Target size={12} className="text-teal-600 shrink-0" />
                              <span className="text-sm flex-1 truncate">{g.title}</span>
                              <span className="text-xs text-muted-foreground">{g.progress || 0}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {participantDetail.snapshot.active_flags?.length > 0 && (
                      <div className="p-3 rounded-xl bg-red-50 border border-red-200">
                        <p className="text-xs font-medium text-red-700 mb-1">Active Flags ({participantDetail.snapshot.flags_count})</p>
                        {participantDetail.snapshot.active_flags.map((f, i) => (
                          <div key={`item-${i}`} className="flex items-center gap-2 mt-1">
                            <AlertTriangle size={12} className="text-red-500" />
                            <span className="text-xs text-red-700">{f.type}: {f.description}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {participantDetail.snapshot.upcoming_appointment && (
                      <div className="p-3 rounded-xl bg-blue-50">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-blue-600" />
                          <span className="text-sm font-medium">Next Appointment</span>
                        </div>
                        <p className="text-xs text-blue-600 mt-0.5">
                          {participantDetail.snapshot.upcoming_appointment.date} at {participantDetail.snapshot.upcoming_appointment.time}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <FileText size={32} className="mx-auto text-slate-200 mb-2" />
                    <p className="text-sm text-muted-foreground">No case snapshot available</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
